import { INSPECTION_STATUS } from '@hetivo/hetivo-lib-types';
import type { InspectionStatus, InspectionTab } from '@hetivo/hetivo-lib-types';
import { z } from 'zod/v4';

// ─── Tabs ───────────────────────────────────────────────────────────────────

/** The Inspections list tabs, in display order (SWOOP-400). `due` is the default. */
export const INSPECTION_TABS = ['due', 'inProgress', 'completed'] as const satisfies readonly InspectionTab[];

/** The status each tab lists (a tab is a status). The one place the two vocabularies meet. */
export const TAB_STATUS: Record<InspectionTab, InspectionStatus> = {
  completed: INSPECTION_STATUS.COMPLETED,
  due: INSPECTION_STATUS.DUE,
  inProgress: INSPECTION_STATUS.IN_PROGRESS,
};

const DEFAULT_TAB: InspectionTab = 'due';

// ─── URL search schema ──────────────────────────────────────────────────────

/**
 * The Inspections list URL search-param schema (SWOOP-400, FR-001). Every field is optional + `.catch(undefined)`
 * so a hand-edited or stale URL degrades to the defaults (applied by the consuming hook) instead of throwing,
 * and a plain navigation to `/inspections` needs no search object. The tab in the URL survives a reload and a
 * shared link.
 */
export const inspectionsListSearchSchema = z.object({
  page: z.number().int().positive().optional().catch(undefined),
  tab: z.enum(INSPECTION_TABS).optional().catch(undefined),
});

export type InspectionsListSearch = z.infer<typeof inspectionsListSearchSchema>;

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Narrows a raw string to an {@link InspectionTab}, falling back to `due`. */
export function toInspectionTab(value: string | undefined): InspectionTab {
  return INSPECTION_TABS.find((tab) => tab === value) ?? DEFAULT_TAB;
}
